import { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	MenuItem,
	Paper,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import {
	BarChart3,
	BookOpen,
	CheckCircle2,
	ChevronDown,
	CircleHelp,
	ClipboardList,
	Flame,
	Stethoscope,
	Target,
} from "lucide-react";
import { getSpecialtyPerformancePageData } from "utils/supabase";
import LogoLoader from "components/Custom/LogoLoader.js";

const periodOptions = [7, 30, 90];

const colorConfig = {
	purple: { main: "#7c3aed", soft: "#f3e8ff", pale: "#f8f5ff", shadow: "rgba(124, 58, 237, 0.18)" },
	blue: { main: "#2563eb", soft: "#dbeafe", pale: "#eff6ff", shadow: "rgba(37, 99, 235, 0.16)" },
	green: { main: "#22c55e", soft: "#dcfce7", pale: "#f0fdf4", shadow: "rgba(34, 197, 94, 0.16)" },
	orange: { main: "#f97316", soft: "#ffedd5", pale: "#fff7ed", shadow: "rgba(249, 115, 22, 0.16)" },
	red: { main: "#ef4444", soft: "#fee2e2", pale: "#fef2f2", shadow: "rgba(239, 68, 68, 0.16)" },
	default: { main: "#64748b", soft: "#f1f5f9", pale: "#f8fafc", shadow: "rgba(100, 116, 139, 0.14)" },
};

const sectionIcons = {
	stethoscope: Stethoscope,
	book: BookOpen,
	clipboard: ClipboardList,
	target: Target,
	flame: Flame,
};

function getColors(color) {
	return colorConfig[String(color || "").toLowerCase()] || colorConfig.default;
}

function getSectionIcon(icon) {
	return sectionIcons[String(icon || "").toLowerCase()] || Stethoscope;
}

function clampPercentage(value) {
	const parsed = Number(value);
	if (!Number.isFinite(parsed)) return 0;
	return Math.max(0, Math.min(100, Math.round(parsed)));
}

function formatInteger(value) {
	return new Intl.NumberFormat("es-MX").format(Number(value) || 0);
}

function SectionCard({ children, sx }) {
	return (
		<Paper
			elevation={0}
			sx={{
				bgcolor: "#ffffff",
				border: "1px solid #e2e8f0",
				borderRadius: "14px",
				boxShadow: "0 16px 42px rgba(15, 23, 42, 0.06)",
				height: "100%",
				...sx,
			}}
		>
			{children}
		</Paper>
	);
}

function SummaryCard({ icon: Icon, color, title, value, subtitle, helper }) {
	const colors = getColors(color);

	return (
		<SectionCard sx={{ p: { xs: 1.55, md: 1.75 } }}>
			<Stack direction="row" alignItems="center" spacing={1.4} sx={{ minWidth: 0 }}>
				<Box sx={{ alignItems: "center", bgcolor: colors.soft, borderRadius: "12px", color: colors.main, display: "flex", flexShrink: 0, height: 44, justifyContent: "center", width: 44 }}>
					<Icon size={22} strokeWidth={2.2} />
				</Box>
				<Box sx={{ minWidth: 0 }}>
					<Typography sx={{ color: "#0f172a", fontSize: 11, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</Typography>
					<Typography sx={{ color: "#0f172a", fontSize: { xs: 22, md: 24 }, fontWeight: 700, lineHeight: 1.05, mt: 0.45 }}>{value}</Typography>
					<Typography sx={{ color: "#64748b", fontSize: 10.5, fontWeight: 500, mt: 0.5 }}>{subtitle}</Typography>
				</Box>
			</Stack>
			{helper ? <Typography sx={{ color: colors.main, fontSize: 10.5, fontWeight: 600, mt: 1.15 }}>{helper}</Typography> : null}
		</SectionCard>
	);
}

function ProgressBar({ value, color = "purple", height = 7 }) {
	const percentage = clampPercentage(value);
	const colors = getColors(color);

	return (
		<Box sx={{ bgcolor: "#e5e7eb", borderRadius: 999, height, overflow: "hidden", width: "100%" }}>
			<Box sx={{ bgcolor: colors.main, borderRadius: 999, height: "100%", width: `${percentage}%` }} />
		</Box>
	);
}

function SectionAccordion({ section, open, onToggle }) {
	const colors = getColors(section?.color);
	const Icon = getSectionIcon(section?.icon);
	const specialties = Array.isArray(section?.specialties) ? section.specialties : [];

	return (
		<SectionCard sx={{ overflow: "hidden" }}>
			<Box onClick={onToggle} sx={{ alignItems: "center", cursor: "pointer", display: "grid", gap: { xs: 1.2, md: 1.6 }, gridTemplateColumns: { xs: "42px minmax(0, 1fr) 58px 28px", sm: "44px minmax(0, 1fr) 86px 92px 28px" }, p: { xs: 1.25, md: 1.5 } }}>
				<Box sx={{ alignItems: "center", bgcolor: colors.soft, borderRadius: "11px", color: colors.main, display: "flex", height: 38, justifyContent: "center", width: 38 }}>
					<Icon size={21} strokeWidth={2.2} />
				</Box>
				<Box sx={{ minWidth: 0 }}>
					<Typography sx={{ color: "#0f172a", fontSize: 15, fontWeight: 700, lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{section?.section_name || "Sección"}</Typography>
					<Typography sx={{ color: "#64748b", fontSize: 11, fontWeight: 500, mt: 0.35 }}>{formatInteger(section?.specialties_studied ?? section?.specialties_count)} especialidades</Typography>
				</Box>
				<Box sx={{ display: { xs: "none", sm: "block" }, minWidth: 0 }}>
					<Typography sx={{ color: "#64748b", fontSize: 10, fontWeight: 700, mb: 0.55 }}>Preguntas</Typography>
					<Typography sx={{ color: "#0f172a", fontSize: 14, fontWeight: 700 }}>{formatInteger(section?.total_items)}</Typography>
				</Box>
				<Box sx={{ minWidth: 0 }}>
					<Typography sx={{ color: "#64748b", display: { xs: "none", sm: "block" }, fontSize: 10, fontWeight: 700, mb: 0.55 }}>Precisión</Typography>
					<Stack direction="row" alignItems="center" spacing={1}>
						<Typography sx={{ color: "#0f172a", fontSize: 13, fontWeight: 700, minWidth: 32 }}>{clampPercentage(section?.accuracy_percentage)}%</Typography>
						<Box sx={{ display: { xs: "none", sm: "block" }, width: 58 }}><ProgressBar value={section?.accuracy_percentage} color={section?.color} height={5} /></Box>
					</Stack>
				</Box>
				<Box sx={{ alignItems: "center", color: "#475569", display: "flex", justifyContent: "center" }}>
					<ChevronDown size={18} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 160ms ease" }} />
				</Box>
			</Box>

			{open ? (
				<Box sx={{ borderTop: "1px solid #e2e8f0", overflowX: "auto" }}>
					<Box sx={{ minWidth: 610 }}>
						<Box sx={{ bgcolor: "#f8fafc", display: "grid", gridTemplateColumns: "minmax(170px, 1.1fr) 82px 150px 82px 82px", px: 1.5, py: 1.1 }}>
							{["Especialidad", "Preguntas", "Precisión", "Correctas", "Incorrectas"].map((label) => (
								<Typography key={label} sx={{ color: "#64748b", fontSize: 10, fontWeight: 700, textAlign: label === "Especialidad" ? "left" : "center" }}>{label}</Typography>
							))}
						</Box>
						{specialties.map((specialty) => {
							return (
								<Box key={specialty?.specialty_id || specialty?.specialty_name} sx={{ alignItems: "center", borderTop: "1px solid #eef2f7", display: "grid", gridTemplateColumns: "minmax(170px, 1.1fr) 82px 150px 82px 82px", px: 1.5, py: 0.95 }}>
									<Box sx={{ minWidth: 0, textAlign: "left" }}>
										<Typography sx={{ bgcolor: colors.pale, border: `1px solid ${colors.soft}`, borderRadius: "6px", color: "#334155", display: "inline-block", fontSize: 11, fontWeight: 600, maxWidth: "100%", overflow: "hidden", px: 0.8, py: 0.35, textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{specialty?.specialty_name}</Typography>
									</Box>
									<Typography sx={{ color: "#334155", fontSize: 12, fontWeight: 700, textAlign: "center" }}>{formatInteger(specialty?.total_items)}</Typography>
									<Box sx={{ alignItems: "center", alignSelf: "stretch", display: "flex", height: "100%", justifyContent: "center" }}>
										<Stack direction="row" alignItems="center" spacing={1.1} sx={{ width: "100%" }}>
											<Typography sx={{ color: "#0f172a", fontSize: 12, fontWeight: 700, width: 34 }}>{clampPercentage(specialty?.accuracy_percentage)}%</Typography>
											<ProgressBar value={specialty?.accuracy_percentage} color={section?.color} height={5} />
										</Stack>
									</Box>
									<Typography sx={{ color: "#16a34a", fontSize: 12, fontWeight: 700, textAlign: "center" }}>{formatInteger(specialty?.correct_items)}</Typography>
									<Typography sx={{ color: "#ef4444", fontSize: 12, fontWeight: 700, textAlign: "center" }}>{formatInteger(specialty?.incorrect_items)}</Typography>
								</Box>
							);
						})}
					</Box>
				</Box>
			) : null}
		</SectionCard>
	);
}

function SectionAccuracyChart({ items }) {
	return (
		<SectionCard sx={{ p: 2 }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
				<Typography sx={{ color: "#0f172a", fontSize: 15, fontWeight: 700 }}>Precisión promedio por sección</Typography>
			</Stack>
			<Stack spacing={1.25}>
				{(items || []).map((item) => {

					return (
						<Stack key={item?.section_name} direction="row" alignItems="center" spacing={1.5}>
							<Typography sx={{ color: "#334155", flexShrink: 0, fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: 112 }}>{item?.section_name}</Typography>
							<ProgressBar value={item?.accuracy_percentage} color={item?.color} height={7} />
							<Stack spacing={0.25} sx={{ alignItems: "flex-end", flexShrink: 0, minWidth: 94 }}>
								<Typography sx={{ color: "#0f172a", fontSize: 12, fontWeight: 700, textAlign: "right" }}>{clampPercentage(item?.accuracy_percentage)}%</Typography>
							</Stack>
						</Stack>
					);
				})}
			</Stack>
		</SectionCard>
	);
}

function DistributionChart({ items, total }) {
	let current = 0;
	const segments = (items || []).map((item) => {
		const colors = getColors(item?.color);
		const start = current;
		current += Number(item?.percentage) || 0;
		return `${colors.main} ${start}% ${current}%`;
	});
	const gradient = segments.length ? segments.join(", ") : "#e2e8f0 0% 100%";

	return (
		<SectionCard sx={{ p: 2 }}>
			<Typography sx={{ color: "#0f172a", fontSize: 15, fontWeight: 700, mb: 2 }}>Distribución de respuestas correctas por sección</Typography>
			<Stack direction={{ xs: "column", sm: "row", lg: "column", xl: "row" }} alignItems="center" spacing={2}>
				<Box sx={{ display: "flex", justifyContent: "center", width: { xs: "100%", sm: "auto", lg: "100%", xl: "auto" } }}>
					<Box sx={{ alignItems: "center", background: `conic-gradient(${gradient})`, borderRadius: "50%", display: "flex", flexShrink: 0, height: 160, justifyContent: "center", width: 160 }}>
						<Box sx={{ alignItems: "center", bgcolor: "#fff", borderRadius: "50%", display: "flex", flexDirection: "column", height: 104, justifyContent: "center", width: 104 }}>
							<Typography sx={{ color: "#0f172a", fontSize: 25, fontWeight: 700, lineHeight: 1 }}>{formatInteger(total)}</Typography>
							<Typography sx={{ color: "#64748b", fontSize: 11, fontWeight: 600 }}>Correctas</Typography>
						</Box>
					</Box>
				</Box>
				<Stack spacing={1.2} sx={{ width: "100%" }}>
					{(items || []).map((item) => {
						const colors = getColors(item?.color);
						return (
							<Stack key={item?.section_name} direction="row" alignItems="center" spacing={1}>
								<Box sx={{ bgcolor: colors.main, borderRadius: "50%", flexShrink: 0, height: 9, width: 9 }} />
								<Typography sx={{ color: "#334155", flex: 1, fontSize: 12, fontWeight: 600, minWidth: 0 }}>{item?.section_name}</Typography>
								<Typography sx={{ color: "#475569", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>{formatInteger(item?.correct_items)} ({clampPercentage(item?.accuracy_percentage)}%)</Typography>
							</Stack>
						);
					})}
				</Stack>
			</Stack>
			<Typography sx={{ color: "#64748b", fontSize: 11, fontWeight: 600, mt: 2, textAlign: "center" }}>Porcentaje de aciertos sobre respuestas correctas e incorrectas</Typography>
		</SectionCard>
	);
}

function TipCard({ items }) {
	const first = Array.isArray(items) && items.length > 0 ? items[0] : null;
	const message = first ? `Enfócate en ${first.section_name}: ${clampPercentage(first.accuracy_percentage)}% de precisión.` : "Enfócate en las secciones con menor precisión para mejorar tu rendimiento general.";

	return (
		<SectionCard sx={{ bgcolor: "#f8f5ff", borderColor: "#ddd6fe", p: 2 }}>
			<Stack direction="row" spacing={1.4}>
				<Box sx={{ alignItems: "center", bgcolor: "#ede9fe", borderRadius: "50%", color: "#7c3aed", display: "flex", flexShrink: 0, height: 34, justifyContent: "center", width: 34 }}><Target size={18} /></Box>
				<Box sx={{ minWidth: 0 }}>
					<Typography sx={{ color: "#7c3aed", fontSize: 13, fontWeight: 700 }}>Tip</Typography>
					<Typography sx={{ color: "#475569", fontSize: 11.5, fontWeight: 500, mt: 0.35 }}>{message}</Typography>
				</Box>
			</Stack>
		</SectionCard>
	);
}

export default function DesempenoEspecialidad() {
	const [days, setDays] = useState(30);
	const [payload, setPayload] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState("");
	const [openSections, setOpenSections] = useState({});
	const [showSplash, setShowSplash] = useState(true);

	useEffect(() => {
		let mounted = true;

		async function fetchPerformance() {
			setIsLoading(true);
			setErrorMessage("");

			try {
				const { data, error } = await getSpecialtyPerformancePageData(days, false);
				if (!mounted) return;

				if (error) {
					console.error("Error fetching specialty performance:", error);
					setErrorMessage(error?.message || "No se pudo cargar el desempeño por especialidad.");
					setPayload(null);
					return;
				}

				setPayload(data || {});
				const initialOpen = {};
				(data?.accordion_sections || []).forEach((section, index) => {
					initialOpen[section?.section_key || section?.section_name || index] = Boolean(section?.is_open_default || index === 0);
				});
				setOpenSections(initialOpen);
			} catch (error) {
				if (!mounted) return;
				console.error("Unexpected error fetching specialty performance:", error);
				setErrorMessage(error?.message || "No se pudo cargar el desempeño por especialidad.");
				setPayload(null);
			} finally {
				if (mounted) setIsLoading(false);
			}
		}

		fetchPerformance();

		return () => {
			mounted = false;
		};
	}, [days]);

	useEffect(() => {
		if (isLoading) return undefined;

		// let the logo mouth-open animation finish before revealing the app
		const timeoutId = setTimeout(() => setShowSplash(false), 950);
		return () => clearTimeout(timeoutId);
	}, [isLoading]);

	const summary = payload?.summary || {};
	const sections = useMemo(() => (Array.isArray(payload?.accordion_sections) ? payload.accordion_sections : []), [payload?.accordion_sections]);
	const chartItems = Array.isArray(payload?.section_accuracy_chart) ? payload.section_accuracy_chart : [];
	const distribution = useMemo(() => (Array.isArray(payload?.specialty_distribution_by_section) ? payload.specialty_distribution_by_section : []), [payload?.specialty_distribution_by_section]);
	const correctDistribution = useMemo(() => {
		const items = sections.map((section, index) => {
			const specialties = Array.isArray(section?.specialties) ? section.specialties : [];
			const fallbackDistributionItem = distribution[index];
			const sectionCorrectItems = Number(section?.correct_items);
			const sectionIncorrectItems = Number(section?.incorrect_items);
			const specialtyCorrectItems = specialties.reduce((acc, specialty) => acc + (Number(specialty?.correct_items) || 0), 0);
			const specialtyIncorrectItems = specialties.reduce((acc, specialty) => acc + (Number(specialty?.incorrect_items) || 0), 0);
			const fallbackCorrectItems = Number(fallbackDistributionItem?.correct_items);
			const fallbackIncorrectItems = Number(fallbackDistributionItem?.incorrect_items);
			const correctItems = Number.isFinite(sectionCorrectItems)
				? sectionCorrectItems
				: specialtyCorrectItems > 0
					? specialtyCorrectItems
					: Number.isFinite(fallbackCorrectItems)
						? fallbackCorrectItems
						: 0;
			const incorrectItems = Number.isFinite(sectionIncorrectItems)
				? sectionIncorrectItems
				: specialtyIncorrectItems > 0
					? specialtyIncorrectItems
					: Number.isFinite(fallbackIncorrectItems)
						? fallbackIncorrectItems
						: 0;
			const totalAnsweredItems = correctItems + incorrectItems;

			return {
				accuracy_percentage: totalAnsweredItems > 0 ? (correctItems / totalAnsweredItems) * 100 : 0,
				color: section?.color || fallbackDistributionItem?.color,
				correct_items: correctItems,
				incorrect_items: incorrectItems,
				section_name: section?.section_name || fallbackDistributionItem?.section_name || `Sección ${index + 1}`,
			};
		});

		const totalCorrectItems = items.reduce((acc, item) => acc + (Number(item?.correct_items) || 0), 0);

		return {
			items,
			total: totalCorrectItems,
			withPercentages: items.map((item) => ({
				...item,
				percentage: totalCorrectItems > 0 ? (item.correct_items / totalCorrectItems) * 100 : 0,
			})),
		};
	}, [distribution, sections]);

	return (
		<Box sx={{ mb: 5 }}>
			<Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between" spacing={2} sx={{ mb: 2.5 }}>
				<Box>
					<Typography sx={{ color: "#0f172a", fontSize: { xs: 26, md: 31 }, fontWeight: 700, lineHeight: 1.05 }}>Desempeño por especialidad</Typography>
					<Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 500, mt: 0.6 }}>Tu rendimiento agrupado por sección</Typography>
				</Box>
				<Stack direction="row" spacing={1.2} sx={{ width: { xs: "100%", md: "auto" } }}>
					<Select
						value={days}
						onChange={(event) => setDays(Number(event.target.value))}
						IconComponent={ChevronDown}
						size="small"
						sx={{ bgcolor: "#fff", borderRadius: "9px", color: "#334155", fontSize: 12, fontWeight: 600, minWidth: 144, ".MuiSelect-select": { alignItems: "center", display: "flex", py: 0.9 }, ".MuiSelect-icon": { height: 16, right: 10, top: "calc(50% - 8px)", width: 16 }, ".MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } }}
					>
						{periodOptions.map((option) => <MenuItem key={option} value={option}>Últimos {option} días</MenuItem>)}
					</Select>
				</Stack>
			</Stack>

			{errorMessage ? <Alert severity="error" sx={{ borderRadius: "12px", mb: 2 }}>{errorMessage}</Alert> : null}

			{showSplash ? (
				<SectionCard sx={{ minHeight: 360, p: 4, width: "100%" }}>
					<div style={{ alignItems: "center", display: "flex", flexDirection: "column", justifyContent: "center", minHeight: 280, width: "100%" }}>
						<LogoLoader size={110} label="Cargando desempeño..." fullScreen={false} done={!isLoading} />
					</div>
				</SectionCard>
			) : (
				<Stack spacing={2}>
					<Box sx={{ display: "grid", gap: 1.6, gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" } }}>
						<SummaryCard icon={BarChart3} color="purple" title="Secciones estudiadas" value={formatInteger(summary?.sections_studied)} subtitle={`de ${formatInteger(summary?.total_sections)} disponibles`} />
						<SummaryCard icon={ClipboardList} color="blue" title="Especialidades" value={formatInteger(summary?.specialties_studied)} subtitle={`de ${formatInteger(summary?.total_specialties)} en total`} />
						<SummaryCard icon={CheckCircle2} color="green" title="Precisión promedio" value={`${clampPercentage(summary?.accuracy_percentage)}%`} subtitle="promedio del periodo" />
						<SummaryCard icon={CircleHelp} color="purple" title="Total de preguntas" value={formatInteger(summary?.total_study_items)} subtitle="Total de preguntas respondidas" />
					</Box>

					<Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.45fr) 360px" }, alignItems: "start" }}>
						<Stack spacing={1.6} sx={{ minWidth: 0 }}>
							{sections.map((section, index) => {
								const key = section?.section_key || section?.section_name || index;
								return (
									<SectionAccordion
										key={key}
										section={section}
										open={Boolean(openSections[key])}
										onToggle={() => setOpenSections((current) => ({ ...current, [key]: !current[key] }))}
									/>
								);
							})}
						</Stack>

						<Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", lg: "1fr" }, "@media (min-width: 768px)": { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }, "@media (min-width: 1200px)": { gridTemplateColumns: "1fr" }, minWidth: 0 }}>
							<SectionAccuracyChart items={chartItems} />
							<DistributionChart items={correctDistribution.withPercentages} total={correctDistribution.total} />
							<TipCard items={payload?.sections_to_improve || []} />
						</Box>
					</Box>
				</Stack>
			)}
		</Box>
	);
}
